import type { Vec } from './structure';

export interface DamageRequest {
  point: Vec;
  radius: number;
  energy: number;
  multiplicity?: number;
  direction?: Vec;
  /** Contact solving already applied its impulse; fracture must not add another. */
  impulseScale?: number;
  damageByCell?: ReadonlyMap<number, number>;
}
interface Pending {
  owner: number;
  amount: number;
  request: DamageRequest;
  sequence: number;
  queuedAt: number;
}

/** A bounded-by-live-cells inbox. Ownership changes move work, never duplicate it.
 * Simultaneous pending hits add HP damage before the one topology transition,
 * matching applyHits' existing semantics. The strongest energy controls fracture.
 */
export class DamageInbox {
  private cells = new Map<number, Pending>();
  private owners = new Map<number, Set<number>>();
  private sequence = 0;
  completed = 0;
  add(id: number, owner: number, amount: number, request: DamageRequest): void {
    const prior = this.cells.get(id);
    if (prior) {
      this.remap(id, owner);
      prior.amount += amount;
      prior.request = {
        ...request,
        energy: Math.max(request.energy, prior.request.energy),
        impulseScale: Math.max(request.impulseScale ?? 1, prior.request.impulseScale ?? 1),
      };
      prior.sequence = ++this.sequence;
      return;
    }
    let ids = this.owners.get(owner);
    if (!ids) this.owners.set(owner, (ids = new Set()));
    ids.add(id);
    this.cells.set(id, { owner, amount, request, sequence: ++this.sequence, queuedAt: performance.now() });
  }
  remap(id: number, owner: number): void {
    const p = this.cells.get(id);
    if (!p || p.owner === owner) return;
    this.unlink(id, p.owner);
    p.owner = owner;
    let ids = this.owners.get(owner);
    if (!ids) this.owners.set(owner, (ids = new Set()));
    ids.add(id);
  }
  private unlink(id: number, owner: number): void {
    const old = this.owners.get(owner);
    old?.delete(id);
    if (!old?.size) this.owners.delete(owner);
  }
  remove(id: number): void {
    const p = this.cells.get(id);
    if (!p) return;
    this.unlink(id, p.owner);
    this.cells.delete(id);
  }
  take(owner: number): DamageRequest | undefined {
    const ids = this.owners.get(owner);
    if (!ids) return;
    const damageByCell = new Map<number, number>();
    let last: Pending | undefined,
      energy = 0,
      impulseScale = 0;
    for (const id of ids) {
      const p = this.cells.get(id)!;
      damageByCell.set(id, p.amount);
      if (!last || p.sequence > last.sequence) last = p;
      energy = Math.max(energy, p.request.energy);
      impulseScale = Math.max(impulseScale, p.request.impulseScale ?? 1);
      this.cells.delete(id);
    }
    this.owners.delete(owner);
    this.completed += ids.size;
    return last ? { ...last.request, energy, impulseScale, damageByCell } : undefined;
  }
  get nextOwner(): number | undefined {
    return this.owners.keys().next().value;
  }
  hasOwner(owner: number): boolean {
    return this.owners.has(owner);
  }
  get size(): number {
    return this.cells.size;
  }
  get ownerCount(): number {
    return this.owners.size;
  }
  get oldestMs(): number {
    const first = this.cells.values().next().value;
    return first ? performance.now() - first.queuedAt : 0;
  }
  clear(): void {
    this.cells.clear();
    this.owners.clear();
    this.completed = this.sequence = 0;
  }
}
